import { defineStore } from 'pinia'
import { reactive } from 'vue'
import { VIDEO_CHAT_DEFAULTS } from '../config/videoChatDefaults'
import { saveForCharacter, loadForCharacter } from '../services/settingsPersistence'

const SCHEMA_VERSION = 1

function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj))
}

function toFiniteNumber(value, fallback = 0) {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

function setMissing(target, source) {
  if (!source || typeof source !== 'object') return
  const keys = Object.keys(source)
  for (const k of keys) {
    const sv = source[k]
    const tv = target[k]
    if (sv && typeof sv === 'object' && !Array.isArray(sv)) {
      if (!tv || typeof tv !== 'object') target[k] = {}
      setMissing(target[k], sv)
    } else if (typeof tv === 'undefined') {
      target[k] = sv
    }
  }
}

export const useVideoChatSettingsStore = defineStore('avatarChatVideoSettings', () => {
  const defaults = reactive(deepClone(VIDEO_CHAT_DEFAULTS))
  const settings = reactive(deepClone(VIDEO_CHAT_DEFAULTS))
  const _saveTimers = new Map()

  // Ephemeral runtime-only field to share inspector Foreground with live view
  // Not persisted; consumers should watch and forward to avatarEngine.updateSettings
  settings.precomputedAvatarCutoutSrc = null
  // Ephemeral runtime-only field for refine hair mask override
  settings.precomputedAvatarHairMaskSrc = null

  function resetToDefaults() {
    const fresh = deepClone(VIDEO_CHAT_DEFAULTS)
    Object.keys(settings).forEach((k) => delete settings[k])
    Object.assign(settings, fresh)
  }

  function applyDefaultsToSettings(targetSettings) {
    if (!targetSettings || typeof targetSettings !== 'object') return
    setMissing(targetSettings, defaults)
  }

  /**
   * Forcefully applies defaults into the provided settings object.
   * When override=true, default values overwrite existing mismatched values.
   */
  function enforceDefaultsOn(targetSettings, { override = true } = {}) {
    if (!targetSettings || typeof targetSettings !== 'object') return
    const d = defaults
    // Top-level simple fields
    if (override || typeof targetSettings.useSegmentation === 'undefined') {
      targetSettings.useSegmentation = !!d.useSegmentation
    }
    if (override || typeof targetSettings.segmentationMaskScale === 'undefined') {
      targetSettings.segmentationMaskScale = d.segmentationMaskScale
    }
    if (override || typeof targetSettings.segmentationMaskThreshold === 'undefined') {
      targetSettings.segmentationMaskThreshold = d.segmentationMaskThreshold
    }
    if (override || typeof targetSettings.segmentationInvertMask === 'undefined') {
      targetSettings.segmentationInvertMask = d.segmentationInvertMask
    }
    if (override || typeof targetSettings.segmentationDebugOverlay === 'undefined') {
      targetSettings.segmentationDebugOverlay = d.segmentationDebugOverlay
    }
    if (override || typeof targetSettings.meshWorkerEnabled === 'undefined') {
      targetSettings.meshWorkerEnabled = d.meshWorkerEnabled
    }
    if (override || typeof targetSettings.highQualityWarp === 'undefined') {
      targetSettings.highQualityWarp = !!d.highQualityWarp
    }
    if (override || typeof targetSettings.overlayMode === 'undefined') {
      targetSettings.overlayMode = d.overlayMode
    }
    if (override || typeof targetSettings.expressionPresetId === 'undefined') {
      targetSettings.expressionPresetId = d.expressionPresetId || 'expressive'
    }
    if (override || typeof targetSettings.expressionStrength === 'undefined') {
      targetSettings.expressionStrength = d.expressionStrength
    }
    if (override || typeof targetSettings.faceFitStrength === 'undefined') {
      targetSettings.faceFitStrength = d.faceFitStrength
    }
    if (override || typeof targetSettings.mouthBoost === 'undefined') {
      targetSettings.mouthBoost = d.mouthBoost
    }
    if (override || typeof targetSettings.mouthSmoothing === 'undefined') {
      targetSettings.mouthSmoothing = d.mouthSmoothing
    }
    if (override || typeof targetSettings.browStrength === 'undefined') {
      targetSettings.browStrength = d.browStrength
    }
    if (override || typeof targetSettings.browSmoothing === 'undefined') {
      targetSettings.browSmoothing = d.browSmoothing
    }
    if (override || typeof targetSettings.cheekStrength === 'undefined') {
      targetSettings.cheekStrength = d.cheekStrength
    }
    if (override || typeof targetSettings.cheekSmoothing === 'undefined') {
      targetSettings.cheekSmoothing = d.cheekSmoothing
    }
    if (override || typeof targetSettings.irisStyle === 'undefined') {
      targetSettings.irisStyle = d.irisStyle
    }
    // Eye
    targetSettings.eye =
      targetSettings.eye && typeof targetSettings.eye === 'object' ? targetSettings.eye : {}
    Object.keys(d.eye || {}).forEach((k) => {
      if (override || typeof targetSettings.eye[k] === 'undefined') {
        targetSettings.eye[k] = d.eye[k]
      }
    })
    // Background (persistable shape)
    const bg =
      targetSettings.background && typeof targetSettings.background === 'object'
        ? targetSettings.background
        : {}
    if (override || typeof bg.id === 'undefined') bg.id = d.background.id
    if (override || typeof bg.src === 'undefined') bg.src = d.background.src
    targetSettings.background = bg
    if (override || typeof targetSettings.backgroundId === 'undefined')
      targetSettings.backgroundId = d.background.id
    if (override || typeof targetSettings.backgroundSrc === 'undefined')
      targetSettings.backgroundSrc = d.background.src
    // Layers
    targetSettings.layerVisibility =
      targetSettings.layerVisibility && typeof targetSettings.layerVisibility === 'object'
        ? targetSettings.layerVisibility
        : {}
    const lv = targetSettings.layerVisibility
    Object.keys(d.layerVisibility || {}).forEach((k) => {
      if (override || typeof lv[k] === 'undefined') lv[k] = d.layerVisibility[k]
    })
  }

  function mergeCharacterOverrides(overrides) {
    if (!overrides || typeof overrides !== 'object') return
    // Shallow merge then fill missing via defaults
    Object.assign(settings, deepClone(overrides))
    setMissing(settings, defaults)
  }

  function update(partial) {
    if (!partial || typeof partial !== 'object') return
    Object.assign(settings, partial)
  }

  function getPersistable() {
    // Minimal document payload we plan to store under character
    return {
      schemaVersion: SCHEMA_VERSION,
      layers: {
        background: !!settings?.layerVisibility?.background,
        camera: !!settings?.layerVisibility?.camera,
        avatarImage: !!settings?.layerVisibility?.avatarImage,
        face: !!settings?.layerVisibility?.avatarFace,
        eyes: !!settings?.layerVisibility?.avatarEyes,
        hair: !!settings?.layerVisibility?.avatarHair
      },
      segmentation: { enabled: !!settings.useSegmentation },
      expression: {
        presetId: settings.expressionPresetId || 'expressive',
        expressionStrength: toFiniteNumber(settings.expressionStrength, 2.46),
        faceFitStrength: toFiniteNumber(settings.faceFitStrength, 0.72),
        mouthBoost: toFiniteNumber(settings.mouthBoost, 3.15),
        mouthSmoothing: toFiniteNumber(settings.mouthSmoothing, 0.42),
        browStrength: toFiniteNumber(settings.browStrength, 1.56),
        browSmoothing: toFiniteNumber(settings.browSmoothing, 0.34),
        cheekStrength: toFiniteNumber(settings.cheekStrength, 3.58),
        cheekSmoothing: toFiniteNumber(settings.cheekSmoothing, 0.32)
      },
      eyes: {
        ...(settings.eye || {}),
        irisStyle: settings.irisStyle || 'vector'
      },
      background: {
        id: settings.backgroundId || settings.background?.id || '',
        src: settings.backgroundSrc || settings.background?.src || null
      },
      updatedAt: Date.now()
    }
  }

  function toPersistable(runtime) {
    const r = runtime || settings
    return {
      schemaVersion: SCHEMA_VERSION,
      layers: {
        background: !!r?.layerVisibility?.background,
        camera: !!r?.layerVisibility?.camera,
        avatarImage: !!r?.layerVisibility?.avatarImage,
        face: !!r?.layerVisibility?.avatarFace,
        eyes: !!r?.layerVisibility?.avatarEyes,
        hair: !!r?.layerVisibility?.avatarHair
      },
      segmentation: { enabled: !!r.useSegmentation },
      expression: {
        presetId: r.expressionPresetId || 'expressive',
        expressionStrength: toFiniteNumber(r.expressionStrength, 2.46),
        faceFitStrength: toFiniteNumber(r.faceFitStrength, 0.72),
        mouthBoost: toFiniteNumber(r.mouthBoost, 3.15),
        mouthSmoothing: toFiniteNumber(r.mouthSmoothing, 0.42),
        browStrength: toFiniteNumber(r.browStrength, 1.56),
        browSmoothing: toFiniteNumber(r.browSmoothing, 0.34),
        cheekStrength: toFiniteNumber(r.cheekStrength, 3.58),
        cheekSmoothing: toFiniteNumber(r.cheekSmoothing, 0.32)
      },
      eyes: {
        ...(r.eye || {}),
        irisStyle: r.irisStyle || 'vector'
      },
      background: {
        id: r.backgroundId || r.background?.id || '',
        src: r.backgroundSrc || r.background?.src || null
      },
      updatedAt: Date.now()
    }
  }

  function toRuntime(persisted) {
    if (!persisted || typeof persisted !== 'object') return {}
    const out = {}
    // (optional) allow future migrations based on schemaVersion
    const persistedVersion =
      typeof persisted.schemaVersion === 'number' ? persisted.schemaVersion : 1
    void persistedVersion
    if (persisted.layers) {
      out.layerVisibility = {
        background: !!persisted.layers.background,
        camera: !!persisted.layers.camera,
        avatarImage:
          typeof persisted.layers.avatarImage !== 'undefined'
            ? !!persisted.layers.avatarImage
            : true,
        avatarFace: !!persisted.layers.face,
        avatarEyes: !!persisted.layers.eyes,
        avatarHair: !!persisted.layers.hair
      }
    }
    if (persisted.segmentation) out.useSegmentation = !!persisted.segmentation.enabled
    if (persisted.expression) {
      out.expressionPresetId = persisted.expression.presetId || 'expressive'
      if (typeof persisted.expression.expressionStrength !== 'undefined')
        out.expressionStrength = toFiniteNumber(persisted.expression.expressionStrength, 2.46)
      if (typeof persisted.expression.faceFitStrength !== 'undefined')
        out.faceFitStrength = toFiniteNumber(persisted.expression.faceFitStrength, 0.72)
      if (typeof persisted.expression.mouthBoost !== 'undefined')
        out.mouthBoost = toFiniteNumber(persisted.expression.mouthBoost, 3.15)
      if (typeof persisted.expression.mouthSmoothing !== 'undefined')
        out.mouthSmoothing = toFiniteNumber(persisted.expression.mouthSmoothing, 0.42)
      if (typeof persisted.expression.browStrength !== 'undefined')
        out.browStrength = toFiniteNumber(persisted.expression.browStrength, 1.56)
      if (typeof persisted.expression.browSmoothing !== 'undefined')
        out.browSmoothing = toFiniteNumber(persisted.expression.browSmoothing, 0.34)
      if (typeof persisted.expression.cheekStrength !== 'undefined')
        out.cheekStrength = toFiniteNumber(persisted.expression.cheekStrength, 3.58)
      if (typeof persisted.expression.cheekSmoothing !== 'undefined')
        out.cheekSmoothing = toFiniteNumber(persisted.expression.cheekSmoothing, 0.32)
    }
    if (persisted.eyes) {
      const { irisStyle, ...eye } = persisted.eyes
      out.eye = eye
      out.irisStyle = irisStyle || 'vector'
    }
    if (persisted.background) {
      out.backgroundId = persisted.background.id || ''
      out.backgroundSrc =
        typeof persisted.background.src !== 'undefined' ? persisted.background.src : null
    }
    return out
  }

  async function saveToCharacter(characterId, runtimeSettings) {
    const payload = toPersistable(runtimeSettings || settings)
    return saveForCharacter(characterId, payload)
  }

  async function loadFromCharacter(characterId) {
    return loadForCharacter(characterId)
  }

  function cancelDebouncedSave(characterId) {
    const id = String(characterId || '').trim()
    const t = _saveTimers.get(id)
    if (t) {
      clearTimeout(t)
      _saveTimers.delete(id)
    }
  }

  function saveToCharacterDebounced(characterId, runtimeSettings, options = {}) {
    const id = String(characterId || '').trim()
    const delay = typeof options.delay === 'number' ? options.delay : 800
    if (!id) return
    cancelDebouncedSave(id)
    const timer = setTimeout(() => {
      _saveTimers.delete(id)
      void saveToCharacter(id, runtimeSettings)
    }, delay)
    _saveTimers.set(id, timer)
  }

  return {
    defaults,
    settings,
    resetToDefaults,
    applyDefaultsToSettings,
    enforceDefaultsOn,
    mergeCharacterOverrides,
    update,
    getPersistable,
    toPersistable,
    toRuntime,
    saveToCharacter,
    loadFromCharacter,
    saveToCharacterDebounced,
    cancelDebouncedSave
  }
})
